import React, { useMemo } from 'react';

import { WidgetProps } from '@rjsf/utils';

import {
  Divider,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';

import { snakeCaseToStartCase } from '~/utils/stringUtils';

const DenseTableCell = styled(TableCell)({
  padding: 8,
  marginRight: 4,
  minWidth: 50,
});

interface LeadTimeRange {
  min_days: number;
  max_days?: number;
}

interface PaymentTemplate {
  id: string;
  name: string;
  description: string;
  payment_schedules: Array<object>;
  minimum_lead_days?: number;
  lead_time_range?: LeadTimeRange;
}

const getHeadingLabel = (heading) => {
  switch (heading) {
    case 'due_date_value':
      return 'Due Days';
    case 'due_date_Type':
      return 'From Booking/Supplier';
    case 'payment_type':
    case 'amount_type':
    case 'amount_value':
    default:
      return snakeCaseToStartCase(heading);
  }
};

function formatLeadTimeRange(leadTimeRange: LeadTimeRange): string {
  const { min_days, max_days } = leadTimeRange;
  if (max_days != null) {
    return `${min_days}–${max_days} days`;
  }
  return `${min_days}+ days`;
}

function PaymentScheduleTemplateDetailsWidget({ value, registry }: WidgetProps) {
  const ids: string[] = registry?.formContext?.paymentScheduleTemplateIds ?? [];
  const singleId = registry?.formContext?.paymentScheduleTemplateId;
  const selectedIds = useMemo(() => {
    if (Array.isArray(ids) && ids.length > 0) return ids;
    if (singleId != null && singleId !== '') return [singleId];
    return [];
  }, [ids, singleId]);

  const scheduleList: Array<PaymentTemplate> = value ?? [];
  const templates = useMemo(() => {
    return selectedIds
      .map((id) => scheduleList.find((t) => t?.id === id))
      .filter((t): t is PaymentTemplate => t != null);
  }, [scheduleList, selectedIds]);

  if (templates.length === 0) {
    return null;
  }

  return (
    <>
      {templates.map((template, index) => (
        <React.Fragment key={template.id}>
          {templates.length > 1 && (
            <>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: index > 0 ? 1 : 0 }}>
                Template {index + 1}
              </Typography>
            </>
          )}
          <TableContainer component={Paper}>
            <TableHead>
              <TableRow>
                <DenseTableCell>ID</DenseTableCell>
                <DenseTableCell>Name</DenseTableCell>
                <DenseTableCell>Description</DenseTableCell>
                {template.lead_time_range && <DenseTableCell>Lead time range</DenseTableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <DenseTableCell>{template.id}</DenseTableCell>
                <DenseTableCell>{template.name}</DenseTableCell>
                <DenseTableCell>{template.description}</DenseTableCell>
                {template.lead_time_range && (
                  <DenseTableCell>{formatLeadTimeRange(template.lead_time_range)}</DenseTableCell>
                )}
              </TableRow>
            </TableBody>
          </TableContainer>
          <TableContainer component={Paper}>
            <TableHead>
              <TableRow>
                <DenseTableCell>Payment Schedule</DenseTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {template.payment_schedules?.map((charge, key) => (
                <TableRow key={key}>
                  <TableCell colSpan={template.lead_time_range ? 4 : 3}>
                    <TableContainer>
                      <TableHead>
                        <TableRow>
                          {Object.keys(charge).map((heading) => (
                            <DenseTableCell key={heading}>{getHeadingLabel(heading)}</DenseTableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          {Object.values(charge).map((val, idx) => (
                            <DenseTableCell key={idx}>{JSON.stringify(val)}</DenseTableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </TableContainer>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </TableContainer>
        </React.Fragment>
      ))}
    </>
  );
}

export default PaymentScheduleTemplateDetailsWidget;
